import classNames from "classnames";
import { xor } from "lodash";
import * as React from "react";
import { useCallback, useState } from "react";
import { usersResx } from "~/resxDist/usersResx";
import { useOnUnmount } from "~/sharedComponents/hooks/useOnUnmount";
import { getItemName, getItemValue, ItemKey } from "~/sharedComponents/list";
import { Confirmation } from "~/sharedComponents/modalPopup/Confirmation";
import { Popover, PopoverProps } from "~/sharedComponents/popover";
import { usePopupStateGetter } from "~/sharedComponents/popover/PopoverPopupStateContext";
import {
    ItemProps,
    MultiSelectList,
    MultiSelectListProps,
} from "~/sharedComponents/select/multiSelect/MultiSelectList";
import { CloseReason } from "~/sharedHOC/ClickOutside/ClickOutside";
import { listInPopoverProps } from "../const/listInPopoverProps";
import { InlineMultiSelectClasses } from "./const";
import { SelectChildren, useSelectTrigger } from "./hooks/useSelectTrigger";
import { ItemWithIsUsedState } from "./types";

export type MultiSelectProps<TItem> = Omit<MultiSelectListProps<TItem>, "onSelectItem"> &
    MultiSelectWithDeleteProps &
    Pick<
        PopoverProps,
        | "openInitially"
        | "width"
        | "minWidth"
        | "matchTriggerWidth"
        | "enableDropDownBehavior"
        | "showOverTrigger"
        | "wrapperClassName"
        | "onToggle"
        | "renderToBody"
        | "enforceFocus"
    > &
    SelectChildren & {
        /** If specified, will be used as content for Popover instead of list of selectable options */
        renderContent?: () => React.ReactNode;
        /**
         * @deprecated do not use this props, it is legacy design, false by default
         */
        inline?: boolean;
        onChange: (value: ItemKey[]) => void;
    };

export const MultiSelect = <TItem,>(props: React.PropsWithChildren<MultiSelectProps<TItem>>) => {
    let {
        inline = false,

        // trigger
        children,
        hideTrigger,

        // input
        value,
        options,
        canCreate,
        onCreate,
        onChange,

        // delete with confirm
        canDelete = false,
        onDelete,
        deleteTitle,
        deleteDescr,

        // popover
        disabled,
        openInitially,
        width,
        minWidth,
        matchTriggerWidth,
        enableDropDownBehavior,
        showOverTrigger,
        wrapperClassName,
        onToggle,
        renderToBody,
        renderContent,
        // list
        isLoading,
        listRowComponent,
        loadOptions,
        renderSelectedItems,
        getItemLabel = getItemName as any,
        getItemKey = getItemValue as any,
        isItemDisabled,
    } = props;

    const [isOpened, setIsOpened] = useState(false);

    const onTogglePopover: PopoverProps["onToggle"] = (value, data) => {
        setIsOpened(value);
        onToggle?.(value, data);
    };

    const trigger = useSelectTrigger({
        children,
        hideTrigger,
        isOpened,
        disabled,
        isLoading,
    });

    const { isDeleteConfirmationOpen, onDeleteWithConfirm, confirmation } = useDeleteWithConfirmation({
        canDelete,
        onDelete,
        deleteDescr,
        deleteTitle,
        getItemKey,
        getItemLabel,
    });

    return (
        <>
            {confirmation}
            <Popover
                disabled={disabled}
                closeOnSearch={true}
                disableClosing={isDeleteConfirmationOpen} // prevents closing popup on click in "Delete" confirmation modal
                openInitially={openInitially}
                width={width}
                minWidth={minWidth}
                matchTriggerWidth={matchTriggerWidth || inline}
                enableDropDownBehavior={enableDropDownBehavior}
                showOverTrigger={showOverTrigger}
                onToggle={onTogglePopover}
                wrapperClassName={classNames(wrapperClassName, inline && InlineMultiSelectClasses.popover)}
                renderToBody={renderToBody}
                ignorePreventedEsc={false} // inlined control only: not to ignore ESC prevented by withEditMode
                preventJqueryEsc={inline} // not to close knockout details
                {...listInPopoverProps}
            >
                {trigger}
                {renderContent?.() || (
                    <PopupMultiSelectList
                        renderSelectedItems={renderSelectedItems}
                        isLoading={isLoading}
                        listRowComponent={listRowComponent}
                        value={value}
                        options={options}
                        onChange={onChange}
                        canCreate={canCreate}
                        onCreate={onCreate}
                        onDelete={onDeleteWithConfirm}
                        loadOptions={loadOptions}
                        getItemKey={getItemKey}
                        getItemLabel={getItemLabel}
                        isItemDisabled={isItemDisabled}
                    />
                )}
            </Popover>
        </>
    );
};

const mergeMultiSelectValue = ({ id, value }: { id: ItemKey; value: ItemKey[] }) =>
    value.includes(id) ? value.filter(x => x !== id) : [...value, id];

type PopupMultiSelectListProps<TItem> = Omit<MultiSelectListProps<TItem>, "onSelectItem"> &
    Pick<MultiSelectProps<TItem>, "onChange">;

const PopupMultiSelectList = <TItem,>({ value: initValue, onChange, ...props }: PopupMultiSelectListProps<TItem>) => {
    const { setValue, save, cancel, value } = useMultiSelectPopupValue({
        value: initValue,
        options: props.options,
        onChange,
        getItemKey: props.getItemKey,
    });

    const getPopupState = usePopupStateGetter();
    useOnUnmount(() => {
        if (props.isLoading) {
            return;
        }

        const isCancelled = getPopupState().lastCloseReason === CloseReason.ESC;
        if (!isCancelled) {
            save();
        } else {
            cancel();
        }
    });

    const onSelectItem = useCallback(
        (item: TItem) => {
            setValue(mergeMultiSelectValue({ value, id: props.getItemKey(item) }));
        },
        [value]
    );

    return <MultiSelectList {...props} onSelectItem={onSelectItem} value={value} />;
};

const useMultiSelectPopupValue = <TItem,>({
    value: currentValue,
    options,
    onChange,
    getItemKey,
}: Pick<MultiSelectListProps<TItem>, "value" | "options" | "getItemKey"> &
    Pick<MultiSelectProps<TItem>, "onChange">) => {
    const [value, setValue] = React.useState(currentValue || []);

    const initValue = React.useRef(currentValue);

    const save = () => {
        // first filter value by existense in options, then compare with initial value;
        // to cover the case when a selected item has been deleted during the editing
        const actualValue = value.filter(v => options.some(o => getItemKey(o) === v));

        if (xor(initValue.current, actualValue).length) {
            // also closes edit mode for inlined control
            onChange(actualValue);
            initValue.current = actualValue;
        }
    };

    const cancel = () => {
        // check if some selected items have been deleted during the editing
        const actualInitialValue = initValue.current.filter(v => options.some(o => getItemKey(o) === v));

        if (xor(initValue.current, actualInitialValue).length) {
            // also closes edit mode for inlined control
            onChange(actualInitialValue);
            initValue.current = actualInitialValue;
        }
    };

    return {
        setValue,
        save,
        cancel,
        value,
    };
};

type MultiSelectWithDeleteProps = ItemProps<any> & {
    canDelete?: boolean;
    onDelete?: (id: ItemKey) => void;
    deleteTitle?: string;
    deleteDescr?: string;
};

const useDeleteWithConfirmation = <TItem,>({
    canDelete,
    onDelete,
    deleteTitle,
    deleteDescr,
    getItemKey,
    getItemLabel,
}: MultiSelectWithDeleteProps) => {
    const itemToDelete = React.useRef<ItemWithIsUsedState<TItem>>();
    const [showConfirmation, setShowConfirmation] = React.useState(false);

    const onDeleteItem = (item: ItemWithIsUsedState<TItem>) => {
        if (item.isUsed) {
            itemToDelete.current = item;
            setShowConfirmation(true);
        } else {
            onDelete(getItemKey(item));
        }
    };

    const onConfirmationSave = () => {
        closeConfirmation();
        onDelete(getItemKey(itemToDelete.current));
    };

    const closeConfirmation = () => {
        setShowConfirmation(false);
    };

    const confirmation = showConfirmation && (
        <Confirmation
            title={`${deleteTitle} ${getItemLabel(itemToDelete.current)} ?`}
            description={deleteDescr}
            disableDelay={3}
            saveBtnText={usersResx.delete}
            save={onConfirmationSave}
            cancel={closeConfirmation}
        />
    );

    return {
        onDeleteWithConfirm: canDelete ? onDeleteItem : null,
        confirmation,
        isDeleteConfirmationOpen: showConfirmation,
    };
};
